import { BottomAction } from '@euler/app/components/BottomAction';
import { Typography } from '@euler/app/components/typography';
import { SiteInspectionManager } from '@euler/app/flows/task/functions/task-detail/inspection/SiteInspectionManager';
import {
  TroubleLightItem,
  WiredTroubleLightCell,
} from '@euler/app/flows/task/pages/pre-inspection/components/TroubleLightCell';
import { Center } from '@euler/components/Center';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  useGeneralBottomSheetHeight,
  useGeneralBottomSheetModal,
} from '@euler/functions/useBottomSheetModal';
import { PreInspection } from '@euler/model/task-detail';
import {
  BottomSheetFlatList,
  BottomSheetFooter,
  BottomSheetFooterProps,
  BottomSheetModal,
} from '@gorhom/bottom-sheet';
import { memo, useCallback } from 'react';
import { FlatList, Platform } from 'react-native';
import {
  interpolate,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

type Props = {
  isVisible?: boolean;
  items: {
    item: TroubleLightItem;
    manager: SiteInspectionManager<PreInspection>;
  }[];
  onDismiss?: () => void;
  onSiteCheckChange: (siteId: number, isDefective: boolean) => void;
  onCommit?: () => void;
};

export const TroubleLightInspectionModal = memo(
  ({ items, isVisible, onSiteCheckChange, onDismiss, onCommit }: Props) => {
    const { rpx } = useResponsiveContext();
    const insets = useSafeAreaInsets();
    const { bottomSheetModalRef, ...bottomSheetProps } =
      useGeneralBottomSheetModal({
        show: isVisible,
        minSnapPoint: '40%',
        mediumSnapPoint: '75%',
        index: 1,
        enableDismissOnClose: true,
        enablePanDownToClose: false,
        dismissWhenPressBackdrop: false,
        onDismiss,
      });
    const animatedPosition = useSharedValue(0);
    const height = useGeneralBottomSheetHeight();
    const style = useAnimatedStyle(() => {
      return {
        opacity: interpolate(
          animatedPosition.value,
          [(height * 2) / 4, height],
          [1, 0],
        ),
      };
    });

    const Footer = useCallback(
      (props: BottomSheetFooterProps) => {
        return (
          <BottomSheetFooter {...props}>
            <BottomAction style={style}>
              <BottomAction.Button
                text="返回"
                variant="default"
                textStyle={{ color: '#333' }}
                onPress={() => bottomSheetModalRef.current?.dismiss()}
              />
              <BottomAction.Button
                text="完成"
                onPress={onCommit}
                css={`
                  margin-left: ${rpx(15)}px;
                `}
              />
            </BottomAction>
          </BottomSheetFooter>
        );
      },
      [bottomSheetModalRef, onCommit, rpx, style],
    );

    // BottomSheet specific scroll view components do not work on Web right now.
    // see https://github.com/gorhom/react-native-bottom-sheet/issues/11.
    const ListComponent =
      Platform.OS === 'web' ? FlatList : BottomSheetFlatList;

    return (
      <BottomSheetModal
        ref={bottomSheetModalRef}
        {...bottomSheetProps}
        footerComponent={Footer}
        animatedPosition={animatedPosition}
      >
        <ListComponent
          data={items}
          keyExtractor={x => x.item.siteCode}
          contentContainerStyle={{
            paddingBottom: insets.bottom + rpx(15),
          }}
          ListHeaderComponent={
            <Center
              css={`
                padding: ${rpx(10)}px 0;
                border-bottom-width: 1px;
                border-bottom-color: #f1f1f1;
              `}
            >
              <Typography.Label size={rpx(20)} color="#333" medium>
                仪表故障灯
              </Typography.Label>
            </Center>
          }
          renderItem={({ item }) => (
            <WiredTroubleLightCell
              item={item.item}
              siteInspectionManager={item?.manager}
              onSiteCheckChange={onSiteCheckChange}
            />
          )}
        />
      </BottomSheetModal>
    );
  },
);
